import * as THREE from 'three'
import Terrain, { BlockType, isFluidType } from './index'
import Block from './mesh/block'

/**
 * Minecraft-style cellular fluid simulation for water and lava.
 *
 * flowLevel encoding on custom Blocks:
 *   8 = source (legacy saves may carry 0 — treated as source)
 *   9 = falling column (full height, sustained by fluid above)
 *   1..7 = horizontal flow, decreasing away from the source
 *
 * Water spreads 1 level per block (reaches 7 blocks), ticks every 250 ms.
 * Lava spreads 2 levels per block (reaches 3 blocks), ticks every 1000 ms.
 *
 * Mixing (vanilla Java rules — the lava cell is the one transformed):
 *   water beside/above a lava SOURCE  -> obsidian
 *   water beside/above flowing lava   -> cobblestone
 *   lava flowing INTO a water cell    -> that cell becomes stone
 *
 * The engine is push-based and queue-driven: cells are only evaluated after
 * something near them changed, so settled fluid costs nothing per frame.
 */

export const SOURCE_LEVEL = 8
export const FALLING_LEVEL = 9

const WATER_TICK_MS = 250
const LAVA_TICK_MS = 1000
const MAX_CELLS_PER_TICK = 256

const HORIZONTAL: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1]
]

const normalizeLevel = (level: number) => (level === 0 ? SOURCE_LEVEL : level)

const levelHeight = (level: number) => {
  if (level >= FALLING_LEVEL) return 1
  if (level >= SOURCE_LEVEL) return 0.889
  return 0.1 + level * 0.095
}

export default class FluidEngine {
  constructor(terrain: Terrain) {
    this.terrain = terrain
  }

  terrain: Terrain

  pending: Record<number, Set<string>> = {
    [BlockType.water]: new Set(),
    [BlockType.lava]: new Set()
  }
  lastTick: Record<number, number> = {
    [BlockType.water]: 0,
    [BlockType.lava]: 0
  }
  instanceIndex: Record<number, Map<string, number>> = {
    [BlockType.water]: new Map(),
    [BlockType.lava]: new Map()
  }
  freeSlots: Record<number, number[]> = {
    [BlockType.water]: [],
    [BlockType.lava]: []
  }

  matrixScratch = new THREE.Matrix4()
  zeroMatrix = new THREE.Matrix4().set(0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0)
  evalScratch: string[] = []

  key = (x: number, y: number, z: number) => `${x}_${y}_${z}`

  drop = (type: BlockType) => (type === BlockType.lava ? 2 : 1)

  opposite = (type: BlockType) =>
    type === BlockType.water ? BlockType.lava : BlockType.water

  schedule = (type: BlockType, x: number, y: number, z: number) => {
    this.pending[type].add(this.key(x, y, z))
  }

  scheduleCellAndNeighbors = (x: number, y: number, z: number) => {
    for (const type of [BlockType.water, BlockType.lava]) {
      this.schedule(type, x, y, z)
      this.schedule(type, x, y + 1, z)
      this.schedule(type, x, y - 1, z)
      for (const [dx, dz] of HORIZONTAL) {
        this.schedule(type, x + dx, y, z + dz)
      }
    }
  }

  // call whenever any block is placed or removed so nearby fluid reacts
  notifyBlockChanged = (x: number, y: number, z: number) => {
    this.scheduleCellAndNeighbors(Math.trunc(x), Math.trunc(y), Math.trunc(z))
  }

  notifyRegion = (
    minX: number,
    minY: number,
    minZ: number,
    maxX: number,
    maxY: number,
    maxZ: number
  ) => {
    for (let x = minX - 1; x <= maxX + 1; x++) {
      for (let y = Math.max(1, minY - 1); y <= maxY + 1; y++) {
        for (let z = minZ - 1; z <= maxZ + 1; z++) {
          this.schedule(BlockType.water, x, y, z)
          this.schedule(BlockType.lava, x, y, z)
        }
      }
    }
  }

  getCustomFluid = (type: BlockType, x: number, y: number, z: number) => {
    const custom = this.terrain.getCustomBlock(x, y, z)
    if (custom && custom.placed && custom.type === type) return custom
    return null
  }

  // -1 when the cell holds no fluid of this type
  fluidLevelAt = (type: BlockType, x: number, y: number, z: number) => {
    const custom = this.terrain.getCustomBlock(x, y, z)
    if (custom) {
      if (custom.placed && custom.type === type) {
        return normalizeLevel(custom.flowLevel)
      }
      if (custom.placed) return -1
      // tombstone — natural fluid cannot exist in a mined-out cell record
      return -1
    }
    return this.terrain.isNaturalFluidAt(type, x, y, z) ? SOURCE_LEVEL : -1
  }

  canFlowInto = (x: number, y: number, z: number) => {
    return this.terrain.canFluidOccupy(x, y, z)
  }

  // ---- instanced-mesh slot management -------------------------------------

  writeInstance = (type: BlockType, x: number, y: number, z: number, level: number) => {
    const k = this.key(x, y, z)
    let index = this.instanceIndex[type].get(k)
    if (index === undefined) {
      const mesh = this.terrain.blocks[type]
      index = this.freeSlots[type].pop() ?? this.terrain.getCount(type)
      if (index >= mesh.instanceMatrix.count) return false
      if (index === this.terrain.getCount(type)) {
        this.terrain.setCount(type)
        mesh.count = this.terrain.getCount(type)
      }
      this.instanceIndex[type].set(k, index)
    }

    const height = levelHeight(level)
    this.matrixScratch.makeScale(1, height, 1)
    this.matrixScratch.setPosition(x, y - (1 - height) / 2, z)
    this.terrain.blocks[type].setMatrixAt(index, this.matrixScratch)
    this.terrain.blocks[type].instanceMatrix.needsUpdate = true
    return true
  }

  clearInstance = (type: BlockType, x: number, y: number, z: number) => {
    const k = this.key(x, y, z)
    const index = this.instanceIndex[type].get(k)
    if (index === undefined) return
    this.instanceIndex[type].delete(k)
    this.freeSlots[type].push(index)
    this.terrain.blocks[type].setMatrixAt(index, this.zeroMatrix)
    this.terrain.blocks[type].instanceMatrix.needsUpdate = true
  }

  onBlocksReset = () => {
    for (const type of [BlockType.water, BlockType.lava]) {
      this.instanceIndex[type].clear()
      this.freeSlots[type].length = 0
      this.pending[type].clear()
    }
  }

  // after the worker replaces all instance buffers, re-add custom fluids
  rebuildAfterRegen = () => {
    for (const type of [BlockType.water, BlockType.lava]) {
      this.instanceIndex[type].clear()
      this.freeSlots[type].length = 0
    }
    this.terrain.syncCustomBlockStore()
    for (const block of this.terrain.customBlocks) {
      if (!block.placed || !isFluidType(block.type)) continue
      this.writeInstance(
        block.type,
        block.x,
        block.y,
        block.z,
        normalizeLevel(block.flowLevel)
      )
    }
  }

  // ---- mutations -----------------------------------------------------------

  setFluid = (type: BlockType, x: number, y: number, z: number, level: number) => {
    const existing = this.terrain.getCustomBlock(x, y, z)
    if (existing && existing.placed && existing.type !== type) return false
    if (!existing || !existing.placed) {
      if (!this.canFlowInto(x, y, z)) return false
    }

    if (!this.writeInstance(type, x, y, z, level)) return false

    const block = this.terrain.recordCustomBlock(
      new Block(x, y, z, type, true, level)
    )
    block.flowLevel = level
    block.placed = true
    this.schedule(type, x, y, z)
    return true
  }

  removeFluid = (type: BlockType, x: number, y: number, z: number) => {
    const block = this.getCustomFluid(type, x, y, z)
    if (!block) return false
    this.clearInstance(type, x, y, z)
    this.terrain.deleteCustomBlock(block)
    this.scheduleCellAndNeighbors(x, y, z)
    return true
  }

  // player/bucket entry point: place a permanent source block
  placeSource = (type: BlockType, x: number, y: number, z: number) => {
    x = Math.trunc(x)
    y = Math.trunc(y)
    z = Math.trunc(z)
    const placed = this.setFluid(type, x, y, z, SOURCE_LEVEL)
    if (placed) this.scheduleCellAndNeighbors(x, y, z)
    return placed
  }

  // bucket pickup — only source blocks can be scooped (vanilla behavior).
  // Ocean water is infinite; natural cave lava is finite and leaves a
  // tombstone (clearedInstance=false tells the caller to hide the
  // worker-rendered instance it raycast into).
  scoop = (
    x: number,
    y: number,
    z: number
  ): { type: BlockType; clearedInstance: boolean } | null => {
    x = Math.trunc(x)
    y = Math.trunc(y)
    z = Math.trunc(z)
    for (const type of [BlockType.water, BlockType.lava]) {
      const custom = this.getCustomFluid(type, x, y, z)
      if (custom) {
        if (normalizeLevel(custom.flowLevel) !== SOURCE_LEVEL) continue
        this.removeFluid(type, x, y, z)
        return { type, clearedInstance: true }
      }
      if (this.terrain.isNaturalFluidAt(type, x, y, z)) {
        if (type === BlockType.water) {
          return { type, clearedInstance: true }
        }
        this.terrain.recordCustomBlock(new Block(x, y, z, type, false))
        this.scheduleCellAndNeighbors(x, y, z)
        return { type, clearedInstance: false }
      }
    }
    return null
  }

  // transform a lava cell that touched water (vanilla: the lava converts)
  solidifyLava = (x: number, y: number, z: number, result: BlockType) => {
    const custom = this.getCustomFluid(BlockType.lava, x, y, z)
    if (custom) {
      this.clearInstance(BlockType.lava, x, y, z)
      this.terrain.deleteCustomBlock(custom)
    }
    // covers natural cave lava too — the solid record shadows it
    this.terrain.placeSolidFromSim(x, y, z, result)
    this.scheduleCellAndNeighbors(x, y, z)
  }

  // checks vanilla's contact directions: water above or beside lava
  resolveLavaContacts = (x: number, y: number, z: number) => {
    const lavaLevel = this.fluidLevelAt(BlockType.lava, x, y, z)
    if (lavaLevel < 0) return false

    let waterAbove = this.fluidLevelAt(BlockType.water, x, y + 1, z) >= 0
    let waterBeside = false
    for (const [dx, dz] of HORIZONTAL) {
      if (this.fluidLevelAt(BlockType.water, x + dx, y, z + dz) >= 0) {
        waterBeside = true
        break
      }
    }
    if (!waterAbove && !waterBeside) return false

    const result =
      lavaLevel === SOURCE_LEVEL ? BlockType.obsidian : BlockType.cobblestone
    this.solidifyLava(x, y, z, result)
    return true
  }

  // ---- simulation ----------------------------------------------------------

  update = () => {
    if (this.terrain.isTerrainGenerating) return
    const now = performance.now()
    if (now - this.lastTick[BlockType.water] >= WATER_TICK_MS) {
      this.lastTick[BlockType.water] = now
      this.tick(BlockType.water)
    }
    if (now - this.lastTick[BlockType.lava] >= LAVA_TICK_MS) {
      this.lastTick[BlockType.lava] = now
      this.tick(BlockType.lava)
    }
  }

  tick = (type: BlockType) => {
    const queue = this.pending[type]
    if (queue.size === 0) return

    const batch = this.evalScratch
    batch.length = 0
    for (const k of queue) {
      batch.push(k)
      if (batch.length >= MAX_CELLS_PER_TICK) break
    }
    for (const k of batch) {
      queue.delete(k)
      const sep1 = k.indexOf('_')
      const sep2 = k.indexOf('_', sep1 + 1)
      const x = Number(k.slice(0, sep1))
      const y = Number(k.slice(sep1 + 1, sep2))
      const z = Number(k.slice(sep2 + 1))
      this.evaluate(type, x, y, z)
    }
  }

  evaluate = (type: BlockType, x: number, y: number, z: number) => {
    if (!this.terrain.isWithinActiveTerrain(x, z)) return

    // lava cells touched by water solidify before anything else
    if (this.resolveLavaContacts(x, y, z)) return
    if (type === BlockType.water) {
      // a water cell schedules adjacent lava for conversion
      if (this.fluidLevelAt(BlockType.lava, x, y - 1, z) >= 0) {
        this.resolveLavaContacts(x, y - 1, z)
      }
      for (const [dx, dz] of HORIZONTAL) {
        if (this.fluidLevelAt(BlockType.lava, x + dx, y, z + dz) >= 0) {
          this.resolveLavaContacts(x + dx, y, z + dz)
        }
      }
    }

    const custom = this.getCustomFluid(type, x, y, z)
    const natural = !custom && this.terrain.isNaturalFluidAt(type, x, y, z)
    if (!custom && !natural) return

    let level = custom ? normalizeLevel(custom.flowLevel) : SOURCE_LEVEL

    // drain check for flowing cells (sources and natural fluid are permanent)
    if (custom && level !== SOURCE_LEVEL) {
      const sustained = this.computeSustained(type, x, y, z)
      if (sustained <= 0) {
        this.removeFluid(type, x, y, z)
        return
      }
      if (sustained !== level) {
        this.setFluid(type, x, y, z, sustained)
        this.scheduleCellAndNeighbors(x, y, z)
        level = sustained
      }

      // infinite water: a flowing cell flanked by 2+ sources becomes a source
      if (type === BlockType.water && this.shouldBecomeSource(x, y, z)) {
        this.setFluid(type, x, y, z, SOURCE_LEVEL)
        this.scheduleCellAndNeighbors(x, y, z)
        level = SOURCE_LEVEL
      }
    }

    this.spread(type, x, y, z, level)
  }

  computeSustained = (type: BlockType, x: number, y: number, z: number) => {
    if (this.fluidLevelAt(type, x, y + 1, z) >= 0) return FALLING_LEVEL

    const drop = this.drop(type)
    let best = 0
    for (const [dx, dz] of HORIZONTAL) {
      const nx = x + dx
      const nz = z + dz
      const neighborLevel = this.fluidLevelAt(type, nx, y, nz)
      if (neighborLevel < 0) continue

      let contribution = 0
      if (neighborLevel === FALLING_LEVEL) {
        // a falling column only feeds sideways once it has landed
        const landed =
          !this.canFlowInto(nx, y - 1, nz) &&
          this.fluidLevelAt(type, nx, y - 1, nz) < 0
        contribution = landed ? SOURCE_LEVEL - drop : 0
      } else if (neighborLevel === SOURCE_LEVEL) {
        contribution = SOURCE_LEVEL - drop
      } else {
        contribution = neighborLevel - drop
      }
      if (contribution > best) best = contribution
    }
    return best
  }

  shouldBecomeSource = (x: number, y: number, z: number) => {
    let sources = 0
    for (const [dx, dz] of HORIZONTAL) {
      const custom = this.getCustomFluid(BlockType.water, x + dx, y, z + dz)
      if (custom && normalizeLevel(custom.flowLevel) === SOURCE_LEVEL) {
        sources++
      } else if (this.terrain.isNaturalWaterAt(x + dx, y, z + dz)) {
        sources++
      }
      if (sources >= 2) break
    }
    if (sources < 2) return false

    // needs solid ground or a water source below
    const belowWater = this.getCustomFluid(BlockType.water, x, y - 1, z)
    if (belowWater && normalizeLevel(belowWater.flowLevel) === SOURCE_LEVEL) {
      return true
    }
    if (this.terrain.isNaturalWaterAt(x, y - 1, z)) return true
    return (
      !this.canFlowInto(x, y - 1, z) &&
      this.fluidLevelAt(BlockType.water, x, y - 1, z) < 0 &&
      this.fluidLevelAt(BlockType.lava, x, y - 1, z) < 0
    )
  }

  spread = (type: BlockType, x: number, y: number, z: number, level: number) => {
    const below = y - 1
    const oppositeType = this.opposite(type)

    // 1. flow downward
    if (this.canFlowInto(x, below, z)) {
      const existing = this.fluidLevelAt(type, x, below, z)
      if (existing < 0) {
        this.setFluid(type, x, below, z, FALLING_LEVEL)
      }
      return
    }

    if (this.fluidLevelAt(type, x, below, z) >= 0) {
      // pouring into our own column — nothing to do here
      return
    }

    if (this.fluidLevelAt(oppositeType, x, below, z) >= 0) {
      if (type === BlockType.lava) {
        // lava flowing into water turns that cell to stone (vanilla)
        this.removeFluid(BlockType.water, x, below, z)
        this.terrain.placeSolidFromSim(x, below, z, BlockType.stone)
        this.scheduleCellAndNeighbors(x, below, z)
      } else {
        this.resolveLavaContacts(x, below, z)
      }
      return
    }

    // 2. blocked below — spread horizontally
    const drop = this.drop(type)
    const spreadLevel =
      level >= SOURCE_LEVEL ? SOURCE_LEVEL - drop : level - drop
    if (spreadLevel < 1) return

    for (const [dx, dz] of HORIZONTAL) {
      const nx = x + dx
      const nz = z + dz

      const oppositeLevel = this.fluidLevelAt(oppositeType, nx, y, nz)
      if (oppositeLevel >= 0) {
        if (type === BlockType.lava) {
          // flowing lava beside water — the lava (this cell) converts
          this.resolveLavaContacts(x, y, z)
          return
        }
        this.resolveLavaContacts(nx, y, nz)
        continue
      }

      const existing = this.fluidLevelAt(type, nx, y, nz)
      if (existing >= 0) {
        // upgrade weaker flows so re-pathing converges
        if (
          existing < spreadLevel &&
          existing !== SOURCE_LEVEL &&
          existing !== FALLING_LEVEL &&
          this.getCustomFluid(type, nx, y, nz)
        ) {
          this.setFluid(type, nx, y, nz, spreadLevel)
        }
        continue
      }

      if (this.canFlowInto(nx, y, nz)) {
        this.setFluid(type, nx, y, nz, spreadLevel)
      }
    }
  }
}
